import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import React, { lazy, Suspense, useEffect } from "react";
import { useAdhocPreviewResultQuery } from "../../generated/graphql";
import AdhocDecisionPanel from "./adhoc-decision-panel";
import AdhocExemplarPanel from "./adhoc-exemplar-panel";
import AdhocUsageLine from "./adhoc-usage-line";

// Lazy so the pdf.js renderer is downloaded only once a run completes and the
// document viewer mounts, not while the run is still pending.
const AdhocDocumentViewer = lazy(async () => import("./adhoc-document-viewer"));

const POLL_INTERVAL_MS = 2000;

const styles = {
  paper: { p: 2 },
  statusRow: { p: 2 },
  runId: { fontFamily: "monospace" },
  viewerFallback: { p: 2 },
  jsonBox: {
    m: 0,
    p: 1.5,
    bgcolor: "grey.100",
    borderRadius: 1,
    fontFamily: "monospace",
    fontSize: 12,
    overflowX: "auto",
    maxHeight: "70vh",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
} satisfies Record<string, SxProps<Theme>>;

const isTerminalStatus = (status: string): boolean =>
  status === "COMPLETED" || status === "FAILED";

const statusColor = (
  status: string,
): "success" | "error" | "info" | "default" => {
  if (status === "COMPLETED") return "success";
  if (status === "FAILED") return "error";
  if (status === "RUNNING" || status === "PENDING") return "info";
  return "default";
};

/**
 * Shown in place of the decision panel when a COMPLETED run has no apply plan.
 * If a plan error is present the run extracted fine but plan derivation failed
 * (the fail-open path) — surface the reason so the operator sees WHY there is no
 * plan, rather than the generic "completed without one" copy.
 */
const PlanlessNotice = ({
  error,
}: {
  readonly error: string | null | undefined;
}) => {
  if (isNil(error)) {
    return (
      <Alert severity="info">
        No decision plan (the run completed without one).
      </Alert>
    );
  }
  return (
    <Alert severity="warning">
      Completed, but the decision plan could not be derived: {error}
    </Alert>
  );
};

/**
 * Polls `adhocPreviewResult` for one triggered run (~2s) until it reaches a
 * terminal status, then renders the completed run's panels: the uploaded
 * document with its OCR overlay, the extracted fields, the side-effect-free
 * decision plan, the retrieved exemplars, and the cost/usage summary. A running
 * run shows a status spinner; a failed run shows its error.
 *
 * @param props The component props.
 * @param props.runId The ad-hoc preview run UUID returned by the trigger.
 */
const AdhocPreviewResult = ({ runId }: { readonly runId: string }) => {
  const { data, loading, error, startPolling, stopPolling, refetch } =
    useAdhocPreviewResultQuery({
      variables: { id: runId },
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    });
  const result = data?.adhocPreviewResult;
  const active = result !== undefined && !isTerminalStatus(result.status);

  useEffect(() => {
    if (active) {
      startPolling(POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [active, startPolling, stopPolling]);

  if (result === undefined) {
    if (error !== undefined) {
      return (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Failed to load run: {error.message}
        </Alert>
      );
    }
    if (loading) {
      return (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={styles.statusRow}
        >
          <CircularProgress size={18} />
          <Typography>Loading run…</Typography>
        </Stack>
      );
    }
    return <Typography sx={styles.statusRow}>Run not found.</Typography>;
  }

  const completed = result.status === "COMPLETED";

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={styles.paper}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="h6">Run</Typography>
          <Chip
            size="small"
            label={result.status}
            color={statusColor(result.status)}
          />
          <Typography variant="body2" color="text.secondary" sx={styles.runId}>
            {result.runId}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            company {result.companyUuid}
          </Typography>
        </Stack>
      </Paper>

      {result.status === "FAILED" && (
        <Alert severity="error">
          {result.error ?? "The run failed without an error detail."}
        </Alert>
      )}

      {active && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={styles.statusRow}
        >
          <CircularProgress size={18} />
          <Typography>Run {result.status.toLowerCase()}…</Typography>
        </Stack>
      )}

      {completed && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={styles.paper}>
              <Typography gutterBottom variant="h6">
                Document
              </Typography>
              <Suspense
                fallback={
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={styles.viewerFallback}
                  >
                    <CircularProgress size={18} />
                    <Typography>Loading viewer…</Typography>
                  </Stack>
                }
              >
                <AdhocDocumentViewer runId={result.runId} />
              </Suspense>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              {isNil(result.applyPlan) ? (
                <PlanlessNotice error={result.error} />
              ) : (
                <AdhocDecisionPanel applyPlan={result.applyPlan} />
              )}
              <AdhocUsageLine usage={result.usage as unknown} />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <AdhocExemplarPanel
              runId={result.runId}
              exemplars={result.exemplars}
            />
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined" sx={styles.paper}>
              <Typography gutterBottom variant="h6">
                Extracted fields
              </Typography>
              {isNil(result.extractedFields) ? (
                <Typography variant="body2" color="text.secondary">
                  No extracted fields recorded.
                </Typography>
              ) : (
                <Box component="pre" sx={styles.jsonBox}>
                  {JSON.stringify(result.extractedFields, null, 2)}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Stack>
  );
};

export default AdhocPreviewResult;
